/**
 * FE-005: WebSocket Signaling Client
 *
 * Manages the WebSocket connection to the DuoCast signaling server.
 * Provides reactive SolidJS signals for connection state, typed message
 * dispatching, heartbeat keepalive, and exponential-backoff reconnection.
 */

import { createSignal, batch } from "solid-js";
import {
  PROTOCOL_VERSION,
  HEARTBEAT_INTERVAL_MS,
  isServerMessage,
} from "@duocast/shared";
import type {
  ClientMessage,
  ServerMessage,
  JoinAttributes,
  JoinPreferences,
  ReportCategory,
  IceCandidate,
} from "@duocast/shared";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConnectionState =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected"
  | "error";

type MessageHandler<T extends ServerMessage = ServerMessage> = (msg: T) => void;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const MAX_RECONNECT_RETRIES = 5;
const PONG_TIMEOUT_MS = 60_000;

// ---------------------------------------------------------------------------
// Signaling Client
// ---------------------------------------------------------------------------

export function createSignalingClient() {
  // -- Reactive state -------------------------------------------------------
  const [connectionState, setConnectionState] =
    createSignal<ConnectionState>("disconnected");
  const [onlineCount, setOnlineCount] = createSignal<number>(0);

  // -- Internal state -------------------------------------------------------
  let ws: WebSocket | null = null;
  let currentUrl: string | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  let pongTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempt = 0;
  let intentionalClose = false;

  const messageQueue: ClientMessage[] = [];
  const handlers = new Map<string, Set<MessageHandler<any>>>();

  // -- Handler registration -------------------------------------------------

  /**
   * Register a handler for a specific server message type.
   * Returns an unsubscribe function.
   */
  function on<T extends ServerMessage>(
    messageType: T["type"],
    handler: MessageHandler<T>,
  ): () => void {
    if (!handlers.has(messageType)) {
      handlers.set(messageType, new Set());
    }
    handlers.get(messageType)!.add(handler as MessageHandler);
    return () => {
      handlers.get(messageType)?.delete(handler as MessageHandler);
    };
  }

  // -- Message dispatch -----------------------------------------------------

  function dispatch(message: ServerMessage): void {
    const typeHandlers = handlers.get(message.type);
    if (typeHandlers) {
      typeHandlers.forEach((handler) => handler(message));
    }
  }

  // -- Heartbeat management -------------------------------------------------

  function startHeartbeat(): void {
    stopHeartbeat();
    heartbeatTimer = setInterval(() => {
      sendRaw({ v: PROTOCOL_VERSION, type: "HEARTBEAT" });

      // Keep a single outstanding PONG timeout from the first unanswered heartbeat.
      if (!pongTimer) {
        pongTimer = setTimeout(() => {
          console.warn("[signaling] PONG timeout — disconnecting");
          cleanupAndReconnect();
        }, PONG_TIMEOUT_MS);
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  function stopHeartbeat(): void {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
    if (pongTimer) {
      clearTimeout(pongTimer);
      pongTimer = null;
    }
  }

  // -- Message send ---------------------------------------------------------

  function sendRaw(message: ClientMessage): void {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  }

  /**
   * Send a client message. If the connection is reconnecting, the message
   * is queued and will be flushed once the connection is restored.
   */
  function send(message: ClientMessage): void {
    const state = connectionState();
    if (state === "reconnecting" || state === "connecting") {
      messageQueue.push(message);
      return;
    }
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  }

  function flushQueue(): void {
    while (messageQueue.length > 0) {
      const msg = messageQueue.shift()!;
      sendRaw(msg);
    }
  }

  // -- Reconnection ---------------------------------------------------------

  function scheduleReconnect(): void {
    if (reconnectAttempt >= MAX_RECONNECT_RETRIES) {
      setConnectionState("error");
      return;
    }

    setConnectionState("reconnecting");
    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt),
      RECONNECT_MAX_MS,
    );
    reconnectAttempt++;

    reconnectTimer = setTimeout(() => {
      if (currentUrl) {
        connectInternal(currentUrl, true);
      }
    }, delay);
  }

  function cleanupAndReconnect(): void {
    stopHeartbeat();
    if (ws) {
      // Prevent onclose from triggering another reconnect cycle
      const oldWs = ws;
      ws = null;
      try {
        oldWs.close();
      } catch {
        // ignore
      }
    }
    if (!intentionalClose) {
      scheduleReconnect();
    }
  }

  // -- Connect --------------------------------------------------------------

  function connectInternal(url: string, isReconnect: boolean): void {
    if (!isReconnect) {
      setConnectionState("connecting");
    }

    const socket = new WebSocket(url);
    ws = socket;

    socket.onopen = () => {
      batch(() => {
        reconnectAttempt = 0;
        setConnectionState("connected");
      });
      startHeartbeat();
      flushQueue();
    };

    socket.onmessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data as string);

        // Basic structural check
        if (!data || typeof data !== "object" || !data.type) {
          return;
        }

        // Use the type guard from shared to validate server messages
        if (isServerMessage(data as any)) {
          const message = data as ServerMessage;

          // Handle PONG specially for online count and heartbeat
          if (message.type === "PONG") {
            if (pongTimer) {
              clearTimeout(pongTimer);
              pongTimer = null;
            }
            setOnlineCount(message.onlineCount);
          }

          dispatch(message);
        }
      } catch {
        // Silently drop unparseable messages
      }
    };

    socket.onclose = () => {
      stopHeartbeat();
      if (ws === socket) {
        ws = null;
      }
      if (!intentionalClose) {
        scheduleReconnect();
      } else {
        setConnectionState("disconnected");
      }
    };

    socket.onerror = () => {
      // onerror is always followed by onclose, so reconnect logic is handled there.
      // No action needed here — onclose will trigger scheduleReconnect().
    };
  }

  /**
   * Connect to the signaling server at the given URL.
   */
  function connect(url: string): void {
    intentionalClose = false;
    currentUrl = url;
    reconnectAttempt = 0;
    messageQueue.length = 0;
    connectInternal(url, false);
  }

  /**
   * Gracefully close the connection.
   */
  function disconnect(): void {
    intentionalClose = true;
    stopHeartbeat();
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    messageQueue.length = 0;
    if (ws) {
      ws.close(1000, "Client disconnect");
      ws = null;
    }
    setConnectionState("disconnected");
  }

  // -- Signaling helpers ----------------------------------------------------

  function join(attributes: JoinAttributes, preferences?: JoinPreferences): void {
    const msg: ClientMessage = {
      v: PROTOCOL_VERSION,
      type: "JOIN",
      attributes,
      ...(preferences && { preferences }),
    };
    send(msg);
  }

  function sendOffer(roomId: string, sdp: string): void {
    send({
      v: PROTOCOL_VERSION,
      type: "OFFER",
      roomId,
      sdp,
    });
  }

  function sendAnswer(roomId: string, sdp: string): void {
    send({
      v: PROTOCOL_VERSION,
      type: "ANSWER",
      roomId,
      sdp,
    });
  }

  function sendIce(roomId: string, candidate: IceCandidate): void {
    send({
      v: PROTOCOL_VERSION,
      type: "ICE",
      roomId,
      candidate,
    });
  }

  function next(roomId: string): void {
    send({
      v: PROTOCOL_VERSION,
      type: "NEXT",
      roomId,
    });
  }

  function report(roomId: string, category: ReportCategory): void {
    send({
      v: PROTOCOL_VERSION,
      type: "REPORT",
      roomId,
      category,
    });
  }

  function revealRequest(roomId: string): void {
    send({
      v: PROTOCOL_VERSION,
      type: "REVEAL_REQUEST",
      roomId,
    });
  }

  // -- Public API -----------------------------------------------------------

  return {
    // Reactive state
    connectionState,
    onlineCount,

    // Connection management
    connect,
    disconnect,

    // Event system
    on,

    // Message sending
    send,

    // Signaling helpers
    join,
    sendOffer,
    sendAnswer,
    sendIce,
    next,
    report,
    revealRequest,
  };
}

export type SignalingClient = ReturnType<typeof createSignalingClient>;
